import { Quill } from "react-quill-new";

const Delta = Quill.import("delta");

type QuillEditor = InstanceType<typeof Quill>;

// tlds is the tail a host with no scheme in front of it is recognised by. It's an
// allow-list so that a file name, a version number or a sentence missing a space
// after its full stop doesn't turn into a link.
const tlds = [
    "com", "org", "net", "edu", "gov", "mil", "int", "info", "biz", "io", "co", "ai",
    "app", "dev", "cloud", "tech", "xyz", "me", "tv", "online", "site", "shop", "blog",
    "news", "live", "link", "page", "pro", "email", "agency", "design", "digital",
    "media", "solutions", "studio", "systems", "works", "world", "space", "store",
    "group", "network",

    "ca", "us", "uk", "au", "nz", "ie", "de", "fr", "es", "it", "nl", "se", "dk", "fi",
    "no", "ch", "at", "be", "pl", "pt", "cz", "gr", "hu", "ro", "tr", "ru", "ua", "il",
    "ae", "za", "br", "mx", "ar", "cl", "jp", "cn", "kr", "in", "sg", "hk", "tw", "th",
    "my", "id", "ph", "vn",
];

// a host with no scheme has to be spelled in the lower case an address is written in.
// Holding it to that is what keeps "Sure.No problem" out of the links.
const bareHost = "(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\\.)+"
    + "(?:" + tlds.join("|") + ")(?![A-Za-z0-9-])"
    + "(?:[:/?#][^\\s<]*)?";

// a mailbox is held to a host carrying a dot, so that a word typed after an @ isn't
// taken for one
const mailbox = "[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9-]+\\.)+[A-Za-z]{2,}";

// urlPattern is what counts as an address: a mailbox, one led by its scheme, or a bare
// host such as google.com. It runs to the end of the word; whatever punctuation closes
// the sentence is taken back off afterwards.
const urlPattern = new RegExp(
    "(?<mail>" + mailbox + ")"
    + "|(?<url>[hH][tT][tT][pP][sS]?://[^\\s<]+|" + bareHost + ")", "g");

// punctuation an address can't end on, so it belongs to the sentence around it
const trailingPunctuation = /[.,;:!?)\]}'"]+$/;

// AutoLink links an address as soon as the word carrying it is finished, and links the
// addresses in text pasted in as plain text.
class AutoLink {
    constructor(quill: QuillEditor) {
        quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => linkOps(delta));

        quill.on(Quill.events.TEXT_CHANGE, (delta, old, source) => {
            if (source !== Quill.sources.USER) return;

            // a space or a newline is what says the word in front of it is finished
            const last = delta.ops[delta.ops.length - 1];
            if (!last || (last.insert !== " " && last.insert !== "\n")) return;

            const first = delta.ops[0];
            const at = typeof first.retain === "number" ? first.retain : 0;
            if (at === 0) return;

            // formatting from inside the handler would re-enter the update quill is
            // part way through, so let this one finish first
            setTimeout(() => linkBefore(quill, at), 0);
        });
    }
}

// linkBefore links the address the text runs up to, where `at` is the character that
// closed it off.
function linkBefore(quill: QuillEditor, at: number) {
    // quill reads the boundary between two lines as the start of the second, so ask
    // about the character before it
    const [line, offset] = quill.getLine(at - 1);
    if (!line) return;

    const start = at - 1 - offset;
    const text = quill.getText(start, offset + 1);

    const found = trailingAddress(text);
    if (!found) return;

    const index = start + found.index;

    // leave whatever was linked by hand alone
    if (quill.getFormat(index, found.text.length).link) return;

    quill.formatText(index, found.text.length, "link", found.href, Quill.sources.USER);
}

// trailingAddress finds the address the text ends on, rather than one further back.
function trailingAddress(text: string) {
    for (const match of text.matchAll(urlPattern)) {
        if (match.index + match[0].length !== text.length) continue;

        const found = address(match);
        if (found) return { ...found, index: match.index };
    }

    return null;
}

// linkOps rewrites a run of pasted text as the plain stretches between its addresses
// and the addresses themselves.
function linkOps(delta: InstanceType<typeof Delta>) {
    const out = new Delta();

    for (const op of delta.ops) {
        if (typeof op.insert !== "string" || op.attributes?.link) {
            out.push(op);
            continue;
        }

        const text = op.insert;
        let plainFrom = 0;

        for (const match of text.matchAll(urlPattern)) {
            const found = address(match);
            if (!found) continue;

            if (match.index > plainFrom) out.insert(text.slice(plainFrom, match.index), op.attributes);
            out.insert(found.text, { ...op.attributes, link: found.href });
            plainFrom = match.index + found.text.length;
        }

        if (plainFrom < text.length) out.insert(text.slice(plainFrom), op.attributes);
    }

    return out;
}

// address reads a match as the text to link and where that link points.
function address(match: RegExpMatchArray) {
    const mail = trim(match.groups?.mail || "");
    if (mail) return { text: mail, href: "mailto:" + mail };

    const url = trim(match.groups?.url || "");
    if (!url) return null;

    // a host given without a scheme is reached over https; the browser would otherwise
    // read it as a path on this site
    return { text: url, href: /^https?:\/\//i.test(url) ? url : "https://" + url };
}

function trim(url: string) {
    return url.replace(trailingPunctuation, "");
}

Quill.register("modules/autoLink", AutoLink);
